import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSun, FiMoon, FiCamera, FiCompass, FiClock, FiMapPin } = FiIcons;

const Safari = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const safariOptions = [
    {
      title: 'Morning Safari',
      time: '5:30 AM - 9:30 AM',
      icon: FiSun,
      price: '$35',
      description: 'The golden hour when elephants are most active. Perfect lighting for photography and cooler temperatures.',
      highlights: ['Best elephant sightings', 'Perfect photography light', 'Cooler weather', 'Bird watching opportunities']
    },
    {
      title: 'Evening Safari',
      time: '2:30 PM - 6:30 PM',
      icon: FiMoon,
      price: '$35',
      description: 'Experience the park as animals prepare for the night. Stunning sunset views over the reservoir.',
      highlights: ['Sunset photography', 'Active wildlife', 'Cooler evening breeze', 'Reservoir views']
    },
    {
      title: 'Full Day Safari',
      time: '5:30 AM - 6:30 PM',
      icon: FiCompass,
      price: '$65',
      description: 'Complete Udawalawe experience with extended time to explore every corner of the park.',
      highlights: ['Maximum wildlife encounters', 'All park zones covered', 'Lunch included', 'Expert commentary']
    }
  ];

  const nilukaSafariFeatures = [
    {
      icon: FiClock,
      title: '40+ Years Experience',
      description: 'Since 1980, Niluka Safari has been the trusted name in Udawalawe wildlife experiences.'
    },
    {
      icon: FiMapPin,
      title: 'Local Expertise',
      description: 'Our guides know every trail, watering hole, and elephant family in the park.'
    },
    {
      icon: FiCamera,
      title: 'Photography Focus',
      description: 'Expert positioning for the perfect wildlife shots and memorable moments.'
    }
  ];

  return (
    <section id="safari" className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Safari with the <span className="gradient-text">Experts</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Experience Udawalawe National Park with Niluka Safari - the original and most trusted 
            safari operator since 1980. Home of Hotel Athgira.
          </p>
        </motion.div>

        {/* Niluka Safari Heritage */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="glass-effect rounded-3xl p-8 mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-playfair font-bold text-gray-900 mb-6">
                Niluka Safari Services
              </h3>
              <p className="text-gray-600 mb-6 font-inter leading-relaxed">
                Hotel Athgira isn't just accommodation - we're the home of Niluka Safari Services, 
                Udawalawe's pioneering safari operator. When you stay with us, you're not just 
                getting a room; you're accessing four decades of wildlife expertise.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                {nilukaSafariFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 liquid-gradient rounded-full flex items-center justify-center mx-auto mb-3">
                      <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2 font-inter">{feature.title}</h4>
                    <p className="text-gray-600 text-sm font-inter">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1549366021-9f761d040a94?w=600&h=400&fit=crop"
                alt="Niluka Safari jeep with elephants"
                className="w-full h-80 object-cover rounded-2xl hover-lift"
              />
              <div className="absolute -bottom-6 -left-6 glass-effect-dark p-4 rounded-xl text-white">
                <div className="text-2xl font-bold">1980</div>
                <div className="text-sm">Since</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Safari Options */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {safariOptions.map((safari, index) => (
            <motion.div
              key={safari.title}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 + index * 0.2 }}
              className="glass-effect rounded-3xl p-6 hover-lift group"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 liquid-gradient rounded-full flex items-center justify-center mx-auto mb-4 morphing-blob group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={safari.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-2">
                  {safari.title}
                </h3>
                <p className="text-orange-600 font-semibold font-inter">{safari.time}</p>
                <div className="text-3xl font-bold text-gray-900 mt-2">{safari.price}</div>
              </div>

              <p className="text-gray-600 mb-6 font-inter text-sm leading-relaxed">
                {safari.description}
              </p>

              <div className="space-y-2 mb-6">
                {safari.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" />
                    <span className="text-gray-700 text-sm font-inter">{highlight}</span>
                  </div>
                ))}
              </div>

              <motion.button
                className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-xl font-semibold ripple-effect"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Book Safari
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Special Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="grid md:grid-cols-2 gap-8"
        >
          <div className="glass-effect rounded-2xl p-6">
            <h4 className="text-xl font-playfair font-bold text-gray-900 mb-4">
              Why Choose Niluka Safari?
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Original safari operator since 1980</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Expert local guides with decades of experience</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Well-maintained vehicles for comfort and safety</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Guaranteed elephant sightings or money back</span>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-2xl p-6">
            <h4 className="text-xl font-playfair font-bold text-gray-900 mb-4">
              What You'll See
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Asian elephants in their natural habitat</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Water buffalo, sambar deer, and wild boar</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Over 180 bird species including eagles and peacocks</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-gray-700 font-inter">Crocodiles and monitor lizards near water bodies</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Safari;